import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  endOfDay,
  endOfMonth,
  isAfter,
  isSameDay,
  isValid,
  startOfDay,
  subDays,
} from 'date-fns';

export type PeriodRange = { start: Date; end: Date };

type PeriodOption = {
  label: string;
  value: PeriodRange;
};

/** Keep a manually edited reporting period valid in one parent-state update. */
export function updatePeriodDate<T extends PeriodOption>(
  option: T,
  field: 'start' | 'end',
  selected: Date,
  reference: PeriodRange = option.value,
): T {
  if (!isValid(selected)) return option;

  let start = startOfDay(field === 'start' ? selected : reference.start);
  let end = endOfDay(field === 'end' ? selected : reference.end);

  if (!isValid(start)) start = startOfDay(selected);
  if (!isValid(end)) end = endOfDay(selected);

  if (isAfter(start, end)) {
    if (field === 'start') {
      const originalStart = startOfDay(reference.start);
      const originalEnd = endOfDay(reference.end);
      if (
        isValid(originalStart) &&
        isValid(originalEnd) &&
        !isAfter(originalStart, originalEnd)
      ) {
        const wholeMonths =
          originalStart.getDate() === 1 &&
          isSameDay(originalEnd, endOfMonth(originalEnd));
        end = wholeMonths
          ? endOfDay(
              subDays(
                addMonths(
                  start,
                  differenceInCalendarMonths(originalEnd, originalStart) + 1,
                ),
                1,
              ),
            )
          : endOfDay(
              addDays(
                start,
                differenceInCalendarDays(originalEnd, originalStart),
              ),
            );
      } else {
        end = endOfDay(start);
      }
    } else start = startOfDay(end);
  }

  return {
    ...option,
    label: 'Custom',
    value: { ...option.value, start, end },
  };
}
